/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import Loader from '../loader';
import { getSafeErrorMessage } from '../../actions/error';
import { SurveyUserRoles } from '../../constants/users';
import { getCurrentUser } from '../../selectors/user';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../reducers';
import { Action } from '../../constants/action';
import G17Client from '../../services/G17Client';
import { UniversalTrackerValuePlain } from '../../types/surveyScope';
import './styles.scss';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { Delegation } from './Delegation';
import { MassDelegation } from '../../types/survey';
import { DelegatedUsers, DelegationModalSurvey } from './types';
import { DelegatedUsersTable } from './DelegatedUsersTable';

const analytics = getAnalytics();

interface DelegationModalProps {
  isOpen: boolean;
  initiativeId: string;
  toggle: () => void;
  handleSubmit?: () => void;
  survey: DelegationModalSurvey;
  utrvs: Pick<UniversalTrackerValuePlain, '_id' | 'stakeholders' | 'initiativeId'>[];
}

type Props = PropsFromRedux & DelegationModalProps;

interface DelegationModalState {
  users: DelegatedUsers;
  loading: boolean;
  message?: string;
  error: boolean;
  errorMessage: string;
  role?: SurveyUserRoles | undefined;
}

type ApiUrls = Pick<MassDelegation, 'role' | 'userId'> & {
  action: Action;
};

class DelegationModal extends Component<Props, DelegationModalState> {
  _isMounted = true;

  static defaultProps = {
    isOpen: false,
    handleSubmit: () => {},
    toggle: () => {},
    initiativeId: '',
    isManager: false,
  };

  handleError = (e: any) => this.setState({ error: true, errorMessage: getSafeErrorMessage(e) });

  state: DelegationModalState = {
    users: { contributors: [], verifiers: [] },
    loading: true,
    error: false,
    errorMessage: '',
  };

  componentDidMount() {
    const { survey, utrvs } = this.props;
    if (survey || utrvs) {
      this.reloadUsers();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { survey, utrvs } = this.props;
    if (!survey) {
      return;
    }

    if (utrvs && prevProps.utrvs && prevProps.utrvs === utrvs) {
      return;
    }

    if (!utrvs && survey && prevProps.survey && prevProps.survey._id === survey._id) {
      return;
    }

    this.reloadUsers();
  }

  reloadUsers() {
    const { survey, utrvs } = this.props;

    let api = undefined;
    if (survey && Array.isArray(utrvs) && utrvs.length > 0) {
      const surveyId = survey._id;
      api = G17Client.post(`surveys/${surveyId}/universal-tracker-values/users`, { utrvIds: utrvs.map((u) => u._id) });
    } else {
      return;
    }

    if (this._isMounted) {
      this.setState({ error: false });
    }

    return api
      .then((response) => {
        if (this._isMounted) {
          this.setState({
            loading: false,
            users: response.data.data ?? [],
          });
        }
      })
      .catch(this.handleError);
  }

  getAnalyticsPayload = (stakeholderType: MassDelegation['role']) => {
    const { initiativeId, survey } = this.props;

    return {
      initiativeId: initiativeId,
      surveyId: survey?._id ?? '',
      scopeLevel: 'utrv',
      scopeGroup: '',
      stakeholderType,
    };
  };

  sendApiRequest = async ({ action, role, userId }: ApiUrls): Promise<any> => {
    const { survey, utrvs } = this.props;

    if (!survey?._id) {
      throw Error('Invalid request');
    }

    if (Array.isArray(utrvs) && utrvs.length > 0) {
      return G17Client.massDelegation(survey._id, action, {
        role,
        utrvIds: utrvs.map((u) => u._id),
        userId,
      });
    }

    throw new Error('Failed to determine api request for delegation');
  };

  handleAddUser = ({ role, userId }: Pick<MassDelegation, 'role' | 'userId'>) => {
    if (!role || !role.length || !userId || !userId.length) {
      return;
    }

    this.setState(
      {
        role: undefined,
        loading: true,
        message: undefined,
      },
      async () => {
        try {
          await this.sendApiRequest({
            action: Action.Add,
            role,
            userId,
          });

          analytics.track(AnalyticsEvents.SurveyDelegationAdded, this.getAnalyticsPayload(role));

          await this.reloadUsers();
          this.props.handleSubmit?.();
        } catch (e) {
          this.handleError(e);
        }
      }
    );
  };

  handleRemoveUser = ({ role, userId }: Pick<MassDelegation, 'role' | 'userId'>) => {
    this.setState(
      {
        loading: true,
        message: undefined,
      },
      async () => {
        try {
          await this.sendApiRequest({
            action: Action.Remove,
            role,
            userId,
          });
          analytics.track(AnalyticsEvents.SurveyDelegationRemoved, this.getAnalyticsPayload(role));

          await this.reloadUsers();
          this.props.handleSubmit?.();
        } catch (e) {
          this.handleError(e);
        }
      }
    );
  };

  startDelegating = (userRole: SurveyUserRoles) => {
    this.setState({ role: userRole });
  };

  stopDelegating = () => {
    this.setState({ role: undefined });
  };

  render() {
    const { isOpen, toggle } = this.props;

    return (
      <Modal isOpen={isOpen} toggle={toggle} backdrop='static' className='modal-lg delegate-form'>
        <ModalHeader toggle={toggle}>Delegation</ModalHeader>
        {this.getModalBody()}
      </Modal>
    );
  }

  getModalBody() {
    const { survey, canAccessVerification, user, utrvs, toggle, initiativeId } = this.props;
    const { loading, error, errorMessage, users, role } = this.state;

    if (error) {
      return <h2>{errorMessage}</h2>;
    }

    if (loading) {
      return (
        <div className='row'>
          <div className='col py-5'>
            <Loader />
          </div>
        </div>
      );
    }

    const delegatedUsersTableProps = {
      canAccessVerification,
      currentUser: user,
      users,
      utrvs,
      survey,
      startDelegating: this.startDelegating,
      handleRemoveUser: this.handleRemoveUser,
      initiativeId
    };

    return (
      <ModalBody>
        {role ? (
          <Delegation
            stopDelegating={this.stopDelegating}
            surveyId={survey?._id}
            delegateCallback={this.handleAddUser}
            role={role}
            delegatedUsers={users}
            utrvsLength={utrvs.length}
            toggle={toggle}
            canAccessVerification={canAccessVerification}
          />
        ) : (
          <DelegatedUsersTable {...delegatedUsersTableProps} />
        )}
      </ModalBody>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: DelegationModalProps) => {
  return {
    user: getCurrentUser(state),
    canAccessVerification: FeaturePermissions.canAccessVerification(state),
  }
}

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DelegationModal);
